import { OpenAIService } from "./openai";
import { ElevenLabsSTTService } from "./elevenLabsSTT";
import { DeepgramSTTService } from "./deepgramSTT";
import { GoogleSTTService } from "./googleSTT";
import { AnthropicSTTService } from "./anthropicSTT";

export interface TranscriptionResult {
  model: string;
  text: string;
  confidence?: number;
  processingTime?: number;
  type?: "transcribe" | "translate";
  error?: boolean;
  provider: "openai" | "elevenlabs" | "deepgram" | "google" | "anthropic";
  isTextTranslation?: boolean;
}

export interface STTConfig {
  openaiApiKey?: string;
  elevenLabsApiKey?: string;
  deepgramApiKey?: string;
  googleApiKey?: string;
  anthropicApiKey?: string;
}

export interface ModelConfig {
  id: string;
  name: string;
  type: "transcribe" | "translate";
  isEnabled: boolean;
  provider: "openai" | "elevenlabs" | "deepgram" | "google" | "anthropic";
  model: string;
  targetLanguage?: string;
}

export class STTManager {
  private openai: OpenAIService | null = null;
  private elevenlabs: ElevenLabsSTTService | null = null;
  private deepgram: DeepgramSTTService | null = null;
  private anthropic: AnthropicSTTService | null = null;
  private googleApiKey: string | null = null;

  constructor(config: STTConfig) {
    if (config.openaiApiKey) {
      this.openai = new OpenAIService({ apiKey: config.openaiApiKey });
    }
    if (config.elevenLabsApiKey) {
      this.elevenlabs = new ElevenLabsSTTService(config.elevenLabsApiKey);
    }
    if (config.deepgramApiKey) {
      this.deepgram = new DeepgramSTTService(config.deepgramApiKey);
    }
    if (config.googleApiKey) {
      this.googleApiKey = config.googleApiKey;
    }
    if (config.anthropicApiKey) {
      this.anthropic = new AnthropicSTTService(config.anthropicApiKey);
    }
  }

  async processAudio(
    audioBlob: Blob,
    model: ModelConfig
  ): Promise<TranscriptionResult> {
    const startTime = performance.now();

    try {
      let result: { text: string; duration?: number };

      switch (model.provider) {
        case "openai":
          if (!this.openai) throw new Error("OpenAI service not initialized");
          result =
            model.type === "translate"
              ? await this.openai.translate(
                  audioBlob,
                  model.targetLanguage || "en",
                  model.model
                )
              : await this.openai.transcribe(audioBlob, model.model);
          break;

        case "elevenlabs":
          if (!this.elevenlabs)
            throw new Error("ElevenLabs service not initialized");
          result = await this.elevenlabs.transcribe(audioBlob);
          break;

        case "deepgram":
          if (!this.deepgram)
            throw new Error("Deepgram service not initialized");
          result = await this.deepgram.transcribe(audioBlob);
          break;

        case "google":
          if (!this.googleApiKey)
            throw new Error("Google service not initialized");
          const googleService = new GoogleSTTService(
            this.googleApiKey,
            model.model,
            model.type
          );
          result =
            model.type === "translate"
              ? await googleService.translate(audioBlob)
              : await googleService.transcribe(audioBlob);
          break;

        case "anthropic":
          if (!this.anthropic)
            throw new Error("Anthropic service not initialized");
          if (model.type !== "translate")
            throw new Error("Anthropic only supports translation");
          if (!this.openai)
            throw new Error("OpenAI service required for transcription");
          const transcription = await this.openai.transcribe(
            audioBlob,
            "whisper-1"
          );
          result = await this.anthropic.translateText(transcription.text);
          break;

        default:
          throw new Error(`Unsupported provider: ${model.provider}`);
      }

      return {
        model: model.name,
        text: result.text,
        processingTime:
          result.duration || (performance.now() - startTime) / 1000,
        type: model.type,
        provider: model.provider,
        isTextTranslation: false,
      };
    } catch (error) {
      console.error(`Error processing audio with ${model.name}:`, error);
      throw error;
    }
  }
}
